import PropTypes from 'prop-types';
import { Card, Divider, Grid, Stack, Tooltip, Typography } from '@mui/material';
import Iconify from '../../../components/Iconify';
import { mrrBreakdown } from '../../../helpers/mrr';

const fEur = (n) =>
  new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

function KpiCard({ label, value, hint, icon, color = 'primary', deltas = [] }) {
  return (
    <Card variant="outlined" sx={{ p: 2.5, height: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Stack
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1.5,
            bgcolor: `${color}.lighter`,
            color: `${color}.main`,
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Iconify icon={icon} width={24} height={24} />
        </Stack>
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" color="text.secondary" noWrap>
              {label}
            </Typography>
            {hint && (
              <Tooltip title={hint}>
                <span style={{ display: 'inline-flex' }}>
                  <Iconify
                    icon="eva:info-outline"
                    width={14}
                    height={14}
                    sx={{ color: 'text.disabled', cursor: 'help' }}
                  />
                </span>
              </Tooltip>
            )}
          </Stack>
          <Typography variant="h4" noWrap>
            {value}
          </Typography>
          {deltas.map((d) => {
            const isUp = d.direction === 'up';
            const deltaColor = isUp ? 'success.main' : 'error.main';
            return (
              <Stack key={d.label} direction="row" spacing={0.25} alignItems="center" sx={{ width: 'fit-content' }}>
                <Iconify
                  icon={isUp ? 'eva:arrow-upward-fill' : 'eva:arrow-downward-fill'}
                  width={16}
                  height={16}
                  sx={{ color: deltaColor }}
                />
                <Typography variant="caption" sx={{ color: deltaColor, fontWeight: 700 }} noWrap>
                  {`${isUp ? '+' : '-'}${fEur(d.value)} ${d.label}`}
                </Typography>
                {d.hint && (
                  <Tooltip title={d.hint}>
                    <span style={{ display: 'inline-flex' }}>
                      <Iconify
                        icon="eva:info-outline"
                        width={13}
                        height={13}
                        sx={{ color: 'text.disabled', cursor: 'help' }}
                      />
                    </span>
                  </Tooltip>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Card>
  );
}

KpiCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  hint: PropTypes.string,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string,
  deltas: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      direction: PropTypes.oneOf(['up', 'down']),
      label: PropTypes.string,
      hint: PropTypes.string,
    })
  ),
};

// Card del MRR de mensuales: MRR actual (valor grande) = MRR base + nuevo MRR del mes.
// El base ya viene sin las bajas programadas, así que no baja a lo largo del mes según se aplican
// las cancelaciones (ver `helpers/mrr.js`). Las bajas se muestran solo como informativas.
function MrrCard({ base, newMrr, current, lostMrr, lostNextMrr }) {
  const pct = base > 0 ? (newMrr / base) * 100 : 0;

  const row = (label, value, hint, color) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {label}
        </Typography>
        <HintIcon title={hint} size={13} />
      </Stack>
      <Typography variant="caption" sx={{ color: color || 'text.primary', fontWeight: 600 }} noWrap>
        {value}
      </Typography>
    </Stack>
  );

  return (
    <Card variant="outlined" sx={{ p: 2.5, height: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Stack
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1.5,
            bgcolor: 'primary.lighter',
            color: 'primary.main',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Iconify icon="eva:credit-card-outline" width={24} height={24} />
        </Stack>
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" color="text.secondary" noWrap>
              MRR actual
            </Typography>
            <HintIcon title="MRR base + nuevo MRR del mes. Solo suscripciones mensuales, sin anuales, y sin contar las bajas programadas que terminan este mes (ya se cobraron el mes pasado)." />
          </Stack>
          <Typography variant="h4" noWrap>
            {fEur(current)}
          </Typography>
          {newMrr > 0 && (
            <Stack direction="row" spacing={0.25} alignItems="center">
              <Iconify icon="eva:arrow-upward-fill" width={14} height={14} sx={{ color: 'success.main' }} />
              <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700 }} noWrap>
                {`+${pct.toFixed(1)}% vs base`}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>

      <Stack spacing={0.5} sx={{ mt: 1.5 }}>
        {row(
          'MRR base',
          fEur(base),
          'MRR con el que se partía este mes, sin las altas del mes y sin las bajas programadas que terminan este mes. Se mantiene estable durante el mes: no baja cuando las cancelaciones se hacen efectivas.'
        )}
        {row(
          'Nuevo MRR',
          `+${fEur(newMrr)}`,
          'MRR de las suscripciones mensuales dadas de alta este mes (sin las que tienen baja programada que termina este mes).',
          'success.main'
        )}
        {(lostMrr > 0 || lostNextMrr > 0) && <Divider sx={{ my: 0.5 }} />}
        {lostMrr > 0 &&
          row(
            'Bajas que terminan este mes',
            fEur(lostMrr),
            'MRR de las mensuales con cancelación programada cuyo periodo termina este mes. Se cobraron el mes pasado y este mes ya no aportan, así que no se cuentan ni en el MRR base ni en el actual.',
            'text.disabled'
          )}
        {lostNextMrr > 0 &&
          row(
            'Bajas que terminan el mes que viene',
            fEur(lostNextMrr),
            'MRR de las mensuales con cancelación programada cuyo periodo termina el mes que viene. Ya han pagado este mes, así que siguen contando en el MRR actual; se restarán el mes siguiente.',
            'text.disabled'
          )}
      </Stack>
    </Card>
  );
}

MrrCard.propTypes = {
  base: PropTypes.number.isRequired,
  newMrr: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
  lostMrr: PropTypes.number.isRequired,
  lostNextMrr: PropTypes.number.isRequired,
};

function HintIcon({ title, size = 14 }) {
  return (
    <Tooltip title={title}>
      <span style={{ display: 'inline-flex' }}>
        <Iconify icon="eva:info-outline" width={size} height={size} sx={{ color: 'text.disabled', cursor: 'help' }} />
      </span>
    </Tooltip>
  );
}

HintIcon.propTypes = {
  title: PropTypes.string.isRequired,
  size: PropTypes.number,
};

export default function SubscriptionsKpis({ stats }) {
  if (!stats) return null;

  const monthlyBilling = Number(stats.monthlyBilling) || 0;

  // Horizonte de renewalsByMonth = 12 meses → cada anual activa aparece 1 vez,
  // así que la suma de `total` es la facturación anual total de las anuales activas.
  const annualBillingTotal = (stats.renewalsByMonth || []).reduce((sum, m) => sum + (Number(m?.total) || 0), 0);

  // Parte mensual = MRR actual (sin bajas programadas), mismo criterio que la card de MRR: si no,
  // el teórico iría bajando durante el mes según se aplican las cancelaciones.
  const mrr = mrrBreakdown(stats);
  const theoreticalMrr = mrr.current + annualBillingTotal / 12;
  const arr = theoreticalMrr * 12;

  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const currentMonthRenewals =
    Number((stats.renewalsByMonth || []).find((m) => m?.month === currentMonthKey)?.total) || 0;
  // Las nuevas suscripciones anuales del mes no están en renewalsByMonth (su renovación cae
  // dentro de un año), pero su primer cobro entra en caja este mes — así que suman aquí.
  const newAnnualThisMonth = Number(stats.newAnnualSubscriptionsThisMonth?.totalEUR) || 0;
  const currentMonthBilling = monthlyBilling + currentMonthRenewals + newAnnualThisMonth;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          label="Facturación del mes"
          value={fEur(currentMonthBilling)}
          icon="eva:pie-chart-2-outline"
          color="warning"
          hint="Cuotas de las mensuales activas (incluidas las que tienen baja programada, que aún cobran este mes) + renovaciones anuales que entran este mes + primer cobro de las nuevas suscripciones anuales del mes. Es la caja recurrente que entra por Stripe en el mes en curso."
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MrrCard
          base={mrr.base}
          newMrr={mrr.newMrr}
          current={mrr.current}
          lostMrr={mrr.lostMrr}
          lostNextMrr={mrr.lostNextMrr}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          label="MRR teórico"
          value={fEur(theoreticalMrr)}
          icon="eva:trending-up-outline"
          color="info"
          hint="MRR actual (mensuales sin las bajas programadas) + facturación anual de las suscripciones anuales activas / 12. Es la métrica SaaS estándar (todo normalizado a base mensual)."
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          label="ARR"
          value={fEur(arr)}
          icon="eva:calendar-outline"
          color="success"
          hint="MRR teórico × 12. Ingresos recurrentes anualizados con la base actual."
        />
      </Grid>
    </Grid>
  );
}

SubscriptionsKpis.propTypes = {
  stats: PropTypes.object,
};
